import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import "./OperationStages.scss";
import { toast } from "react-toastify";
import IconButton from "../../components/IconButton/IconButton";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import { Icon } from "@iconify/react";
import { useOperationsWebSocket } from "../../hooks/useOperationsWebSocket";
import EnquiryBOMModal from "../../components/EnquiryStageHistory/EnquiryBOMModal";
import MinutesOfMeetingManager from "../../components/EnquiryStageHistory/MinutesOfMeetingManager";

// Import Lucide React icons
import { FileText, Speech, ChevronDown } from "lucide-react";

// ADD: Each kanban column is now backed by its own status filter against
// /operation-stage-histories/employee/details?statuses=... . "Due" shows
// both PROCESSING and RETURNED jobs; "Assigned" and "Completed" map 1:1 to
// their own status. PENDING is not used anywhere in this board.
const COLUMN_STATUSES = {
  due: ["PROCESSING", "RETURNED"],
  assigned: ["ASSIGNED"],
  completed: ["COMPLETED"],
};
const ALL_SUMMARY_STATUSES = [
  "PENDING",
  "PROCESSING",
  "ASSIGNED",
  "COMPLETED",
  "RETURNED",
];

const OperationStages = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [returnRemarks, setReturnRemarks] = useState("");
  const [processingAction, setProcessingAction] = useState(false);

  const [showBOMModal, setShowBOMModal] = useState(false);
  const [bomOperationData, setBomOperationData] = useState(null);

  // ADD: Expanded cards state
  const [expandedCards, setExpandedCards] = useState({});

  // ADD: Per-column data + infinite-scroll pagination state for the
  // /operation-stage-histories/employee/details endpoint. Each column
  // (due/assigned/completed) is fetched independently with its own
  // `statuses` filter, and tracks its own nextPage/hasMore. The API takes
  // `page` (0-based on first load, then 1, 2, 3... as the user scrolls) and
  // `size` instead of fromRecord/toRecord.
  const PAGE_SIZE = 10;
  const makeInitialPagination = () => ({
    nextPage: 0,
    hasMore: true,
  });
  const [columnItems, setColumnItems] = useState({
    due: [],
    assigned: [],
    completed: [],
  });
  const [columnPagination, setColumnPagination] = useState({
    due: makeInitialPagination(),
    assigned: makeInitialPagination(),
    completed: makeInitialPagination(),
  });
  const [columnLoadingMore, setColumnLoadingMore] = useState({
    due: false,
    assigned: false,
    completed: false,
  });
  // Refs mirror the state above so the scroll handler (which can fire many
  // times in quick succession) always reads the latest values instead of a
  // stale closure, and so we don't fire overlapping requests per column.
  const columnPaginationRef = useRef(columnPagination);
  const columnItemsRef = useRef(columnItems);
  const columnLoadingRef = useRef({
    due: false,
    assigned: false,
    completed: false,
  });
  useEffect(() => {
    columnPaginationRef.current = columnPagination;
  }, [columnPagination]);

  useEffect(() => {
    columnItemsRef.current = columnItems;
  }, [columnItems]);

  // ADD: Status counts (for the kanban column headers) come from the
  // dedicated /operation-stage-histories/employee/status-summary endpoint
  // rather than from however many rows happen to be loaded client-side.
  const [statusCounts, setStatusCounts] = useState({});

  // ADD: Per-card "fetch full details on expand" state
  // (/operation-stage-histories/employee/{operationStageHistoryId})
  const [detailsLoading, setDetailsLoading] = useState({});
  // Tracks which operation ids currently have a full-details fetch in
  // flight, so the auto-fetch effect below (which re-runs whenever the
  // assigned column's item list changes) never fires a duplicate request
  // while one is already running. Cleared once each fetch settles — unlike
  // a "requested once" flag, this lets a later refreshAll() (which resets
  // detailsFetched back to false) trigger a fresh fetch instead of getting
  // permanently stuck.
  const detailsInFlightRef = useRef({});

  // ADD: Expanded MOM decisions state (per decision, so long ones can be
  // individually expanded/collapsed without affecting the rest of the card)
  const [expandedDecisions, setExpandedDecisions] = useState({});
  const DECISION_TRUNCATE_LENGTH = 140;

  // ADD: Logged-in employee's operation categories/stages (for pill filters)
  const [employeeCategories, setEmployeeCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedStageId, setSelectedStageId] = useState(null);

  // ADD: Status filter for the board (All / Pending / Completed).
  // Pending = Due (PROCESSING, RETURNED) + Assigned columns.
  const [statusFilter, setStatusFilter] = useState("ALL");

  // ADD: "My Tasks" modal — data from
  // /operation-stage-histories/assigned-employees
  const [showMyTasks, setShowMyTasks] = useState(false);
  const [myTasks, setMyTasks] = useState([]);
  const [myTasksLoading, setMyTasksLoading] = useState(false);
  const [myTasksError, setMyTasksError] = useState(null);
  const [myTasksStatusFilter, setMyTasksStatusFilter] = useState("ALL");
  const [myTasksOnlyMine, setMyTasksOnlyMine] = useState(true);
  // Date range for the assigned-employees API (startDate / endDate, yyyy-MM-dd).
  // Presets: WTD (week-to-date, Monday start), MTD, YTD, or CUSTOM.
  const [myTasksRange, setMyTasksRange] = useState("MTD");
  const [myTasksFrom, setMyTasksFrom] = useState("");
  const [myTasksTo, setMyTasksTo] = useState("");

  // MOM Modal State
  const [momModal, setMomModal] = useState({
    isOpen: false,
    operationStageHistoryId: null,
    enquiryId: null,
  });

  const getApiEndpoint = (baseEndpoint) => {
    return `${process.env.REACT_APP_API_OPERATIONS}${baseEndpoint}`;
  };

  // ADD: Paginated fetch against /operation-stage-histories/employee/details
  // for a single column. Each column passes its own `statuses` filter
  // (see COLUMN_STATUSES) so the server does the status filtering instead
  // of us pulling every status into one big list and splitting it client-side.
  // reset=true → start over from page 1 (initial load, refresh, websocket
  // update). reset=false → fetch the next window and append it (infinite
  // scroll as the user scrolls down inside that column).
  const fetchColumn = useCallback(async (columnKey, reset = false) => {
    if (columnLoadingRef.current[columnKey]) return;
    if (!reset && !columnPaginationRef.current[columnKey]?.hasMore) return;

    columnLoadingRef.current[columnKey] = true;
    if (reset) {
      setLoading(true);
    } else {
      setColumnLoadingMore((prev) => ({ ...prev, [columnKey]: true }));
    }
    setError(null);

    const currentPagination = reset
      ? makeInitialPagination()
      : columnPaginationRef.current[columnKey];
    const page = reset ? 0 : currentPagination.nextPage;

    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_OPERATIONS}/operation-stage-histories/employee/details`,
        {
          params: {
            statuses: COLUMN_STATUSES[columnKey],
            page,
            size: PAGE_SIZE,
          },
        },
      );
      if (response.data?.status === 200) {
        const newItems = (response.data.data || []).map((op) => ({
          ...op,
          operationStageCheckListHistories:
            op.operationStageCheckListHistories || [],
          customSpecificationHistories: op.customSpecificationHistories || [],
          productSpecificationHistories: op.productSpecificationHistories || [],
        }));

        setColumnItems((prev) => {
          if (reset) return { ...prev, [columnKey]: newItems };
          const existingIds = new Set(prev[columnKey].map((o) => o.id));
          return {
            ...prev,
            [columnKey]: [
              ...prev[columnKey],
              ...newItems.filter((o) => !existingIds.has(o.id)),
            ],
          };
        });

        setColumnPagination((prev) => ({
          ...prev,
          [columnKey]: {
            nextPage: page + 1,
            hasMore: newItems.length === PAGE_SIZE,
          },
        }));
      } else if (reset) {
        setError("Failed to fetch operation stage histories");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to fetch operation stage histories",
      );
      console.error(`Error fetching "${columnKey}" column:`, err);
    } finally {
      setLoading(false);
      setColumnLoadingMore((prev) => ({ ...prev, [columnKey]: false }));
      columnLoadingRef.current[columnKey] = false;
    }
  }, []);

  // ADD: Fetch/reset all three columns at once (initial load, refresh button,
  // websocket update, or after any mutating action).
  const fetchAllColumns = useCallback(
    (reset = false) => {
      Object.keys(COLUMN_STATUSES).forEach((columnKey) =>
        fetchColumn(columnKey, reset),
      );
    },
    [fetchColumn],
  );

  // ADD: Non-destructive resync for a single column. Unlike fetchColumn's
  // reset=true path (which truncates back to page 0 / PAGE_SIZE items and
  // replaces the array outright), this re-requests exactly as many items as
  // are already loaded, then merges by id into the existing state — so it
  // doesn't drop any pages the user scrolled/"Show more"d in, doesn't move
  // the scroll position, and doesn't blow away an already-expanded card's
  // fetched detail (checklist, custom specs, product specs, MOM). Used for
  // "something changed, resync in the background" cases — mutating actions
  // and websocket pushes — as opposed to a genuine fresh start.
  const fetchColumnQuiet = useCallback(async (columnKey) => {
    if (columnLoadingRef.current[columnKey]) return;
    const loadedCount = columnItemsRef.current[columnKey]?.length || 0;
    const size = Math.max(loadedCount, PAGE_SIZE);

    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_OPERATIONS}/operation-stage-histories/employee/details`,
        {
          params: {
            statuses: COLUMN_STATUSES[columnKey],
            page: 0,
            size,
          },
        },
      );
      if (response.data?.status !== 200) return;

      const freshItems = response.data.data || [];

      setColumnItems((prev) => {
        const existingById = new Map(prev[columnKey].map((o) => [o.id, o]));
        const merged = freshItems.map((freshOp) => {
          const existing = existingById.get(freshOp.id);
          // Once a card exists locally at all — not just once its details
          // have finished loading — the quiet resync must never touch its
          // detail fields. If it only guarded on `existing.detailsFetched`,
          // a resync landing in the window *while* the dedicated full-detail
          // fetch was still in flight would fall through to the branch
          // below and overwrite checklist/specs/MOM with the lightweight
          // (empty) list payload — a race that could wipe data that either
          // just arrived or was about to. Only the full-detail fetch and
          // local optimistic updates are allowed to write these fields now.
          if (existing) {
            return {
              ...existing,
              ...freshOp,
              operationStageCheckListHistories:
                existing.operationStageCheckListHistories,
              customSpecificationHistories:
                existing.customSpecificationHistories,
              productSpecificationHistories:
                existing.productSpecificationHistories,
              minutesOfMeeting: existing.minutesOfMeeting,
              detailsFetched: existing.detailsFetched,
            };
          }
          return {
            ...freshOp,
            operationStageCheckListHistories:
              freshOp.operationStageCheckListHistories || [],
            customSpecificationHistories:
              freshOp.customSpecificationHistories || [],
            productSpecificationHistories:
              freshOp.productSpecificationHistories || [],
          };
        });
        return { ...prev, [columnKey]: merged };
      });

      setColumnPagination((prev) => ({
        ...prev,
        [columnKey]: {
          nextPage: Math.ceil(size / PAGE_SIZE),
          hasMore: freshItems.length === size,
        },
      }));
    } catch (err) {
      console.error(`Error quietly refreshing "${columnKey}" column:`, err);
    }
  }, []);

  const fetchAllColumnsQuiet = useCallback(() => {
    Object.keys(COLUMN_STATUSES).forEach((columnKey) =>
      fetchColumnQuiet(columnKey),
    );
  }, [fetchColumnQuiet]);

  // ADD: Fetch the per-status counts shown in the kanban column headers from
  // /operation-stage-histories/employee/status-summary. This is independent
  // of how many rows are actually loaded into each column client-side.
  const fetchStatusCounts = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_OPERATIONS}/operation-stage-histories/employee/status-summary`,
        { params: { statuses: ALL_SUMMARY_STATUSES } },
      );
      if (response.data?.status === 200) {
        const counts = {};
        (response.data.data || []).forEach((entry) => {
          // API returns status in title case ("Processing", "Returned", ...)
          // even though the docs show it uppercase — normalize so lookups
          // like statusCounts.PROCESSING always work regardless of casing.
          const key = (entry.status || "").toUpperCase();
          counts[key] = entry.totalCount;
        });
        setStatusCounts(counts);
      }
    } catch (err) {
      console.error("Error fetching operation status summary:", err);
    }
  }, []);

  // ADD: Convenience helper used after any mutating action (assign, complete,
  // return, MOM/BOM updates) — quietly resyncs all three columns (without
  // truncating loaded pages or losing scroll/expanded state) and refreshes
  // the header counts.
  const refreshAll = useCallback(() => {
    fetchAllColumnsQuiet();
    fetchStatusCounts();
  }, [fetchAllColumnsQuiet, fetchStatusCounts]);

  useEffect(() => {
    if (user) {
      fetchAllColumns(true);
      fetchStatusCounts();
      fetchEmployeeCategories();
    }
  }, [user, fetchAllColumns, fetchStatusCounts]);

  // WebSocket: pushes real-time notifications whenever any of the
  // operation-stage endpoints (checklist/custom-spec completion, stage
  // complete/accept/return) affect this employee's jobs, so the board stays
  // current without a manual refresh.
  //
  // IMPORTANT: everything below is memoized. Previously this whole options
  // object (including the inline onUpdate function) was a fresh object
  // literal on every render. If useOperationsWebSocket re-subscribes
  // whenever its options identity changes, that created a feedback loop:
  // a message triggers onUpdate -> setColumnItems -> re-render -> new
  // options object -> hook tears down/re-subscribes -> the *next* message
  // now fires two live subscriptions -> two onUpdate calls -> two more
  // re-renders -> three subscriptions... which is exactly the "same
  // message logged 4x in a row, getting worse" pattern we were seeing.
  // Each of those extra fetches replaces columnItems with a freshly
  // ordered array, which is what read as "still refreshing" / losing
  // scroll and the expanded card.
  const tenantId = sessionStorage.getItem("tenantId");
  const empId = user?.empId ?? user?.id;

  const handleWebSocketUpdate = useCallback(() => {
    if (suppressWsRefreshRef.current) {
      // We just completed a checklist item / custom spec ourselves and
      // already applied that change locally — this event is that action's
      // own echo coming back over the socket. Skip the refetch entirely so
      // it can't reorder cards or reset scroll/expanded state for
      // something we already know about.
      return;
    }
    // Was fetchAllColumns(true) — a hard reset back to page 0 for every
    // column. Any backend event fires this, including the echo of the
    // user's own actions (e.g. completing a checklist item triggers a
    // websocket push same as anyone else's change would), so that hard
    // reset was undoing the "don't disturb scroll/expanded state" fix on
    // the mutation handlers themselves. Quiet resync fixes it at the
    // source for every trigger, not just the ones we can see client-side.
    fetchAllColumnsQuiet();
    fetchStatusCounts();
  }, [fetchAllColumnsQuiet, fetchStatusCounts]);

  const webSocketOptions = useMemo(
    () => ({
      // `user` (from AuthContext) is just the raw /api/access response and
      // never carries tenantId — tenantId only ever lives in sessionStorage
      // (set during session bootstrap), which is also what
      // WebSocketService.getAuthHeaders() already reads from for the STOMP
      // connect headers. Read it from the same place here.
      tenantId,
      // Different pages in this app key the logged-in employee under
      // different fields (Enquiries.jsx uses user.empId, TeamEnquiry.jsx
      // uses user.id) — fall back to user.id so this still connects if
      // user.empId isn't populated for this route/role.
      empId,
      isTeamView: false,
      toastMessage: "Your operation has been updated",
      onUpdate: handleWebSocketUpdate,
    }),
    [tenantId, empId, handleWebSocketUpdate],
  );

  const { isWebSocketConnected } = useOperationsWebSocket(webSocketOptions);

  // ADD: Called on scroll inside a kanban column's card list. Fetches the
  // next page for that specific column once the user scrolls near the bottom.
  const handleColumnScroll = useCallback(
    (columnKey) => (e) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
      if (
        nearBottom &&
        columnPaginationRef.current[columnKey]?.hasMore &&
        !columnLoadingRef.current[columnKey]
      ) {
        fetchColumn(columnKey, false);
      }
    },
    [fetchColumn],
  );

  // ADD: Fetch the full detail record for a single operation stage history
  // (all product specs, checklist items, custom specs, and full MOM
  // topics/decisions) and merge it into the already-loaded card.
  const fetchOperationFullDetails = async (operationId) => {
    if (detailsInFlightRef.current[operationId]) return;
    detailsInFlightRef.current[operationId] = true;
    setDetailsLoading((prev) => ({ ...prev, [operationId]: true }));
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_OPERATIONS}/operation-stage-histories/employee/${operationId}`,
      );
      if (response.data?.status === 200 && response.data?.data) {
        const stageData = response.data.data;
        const fullHistory =
          (stageData.operationStageHistories || []).find(
            (h) => h.id === operationId,
          ) || stageData.operationStageHistories?.[0];

        if (fullHistory) {
          // Normalize the full-detail MOM shape (decisionTakenResponseDTOList /
          // topicsResponseDTOList) so the existing "decisions" rendering keeps
          // working, while also keeping the full topics list for display.
          const normalizedMom = fullHistory.minutesOfMeeting
            ? {
                ...fullHistory.minutesOfMeeting,
                decisions:
                  fullHistory.minutesOfMeeting.decisionTakenResponseDTOList ||
                  fullHistory.minutesOfMeeting.decisions ||
                  [],
                // API returns this as `topics` (see the employee/{id} sample
                // response) — `topicsResponseDTOList` was never a real field,
                // so this always came back empty. Fall back to it anyway in
                // case some other endpoint variant does use that name.
                topics:
                  fullHistory.minutesOfMeeting.topics ||
                  fullHistory.minutesOfMeeting.topicsResponseDTOList ||
                  [],
              }
            : undefined;

          // The updated operation could live in any one of the three
          // columns, so merge it in wherever its id is found.
          const mergeDetails = (op) =>
            op.id === operationId
              ? {
                  ...op,
                  ...fullHistory,
                  operationCategoryId:
                    stageData.operationCategoryId ?? op.operationCategoryId,
                  operationStageId:
                    stageData.operationStageId ?? op.operationStageId,
                  operationStageName:
                    stageData.operationStageName || op.operationStageName,
                  stageDescription:
                    stageData.description || op.stageDescription,
                  minutesOfMeeting: normalizedMom || op.minutesOfMeeting,
                  detailsFetched: true,
                }
              : op;

          setColumnItems((prev) => ({
            due: prev.due.map(mergeDetails),
            assigned: prev.assigned.map(mergeDetails),
            completed: prev.completed.map(mergeDetails),
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching operation full details:", err);
      toast.error("Failed to load full details for this job");
    } finally {
      detailsInFlightRef.current[operationId] = false;
      setDetailsLoading((prev) => ({ ...prev, [operationId]: false }));
    }
  };

  // ADD: Fetch the operation categories/stages mapped to the logged-in employee.
  // Used to render the pill filters at the top of the board.
  const fetchEmployeeCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_OPERATIONS}/operation-stage-histories/logged-in-employee`,
      );
      if (response.data?.status === 200 && response.data?.data) {
        setEmployeeCategories(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching employee operation categories:", err);
    } finally {
      setLoadingCategories(false);
    }
  };

  // ADD: "My Tasks" button handler — hits the assigned-employees endpoint
  // every time it is opened so the list is always fresh.
  // yyyy-MM-dd in LOCAL time (toISOString would shift the day for non-UTC users)
  const toDateParam = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const getMyTasksDateRange = () => {
    const today = new Date();
    if (myTasksRange === "WTD") {
      const start = new Date(today);
      const daysSinceMonday = (today.getDay() + 6) % 7;
      start.setDate(today.getDate() - daysSinceMonday);
      return { startDate: toDateParam(start), endDate: toDateParam(today) };
    }
    if (myTasksRange === "MTD") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: toDateParam(start), endDate: toDateParam(today) };
    }
    if (myTasksRange === "YTD") {
      const start = new Date(today.getFullYear(), 0, 1);
      return { startDate: toDateParam(start), endDate: toDateParam(today) };
    }
    return { startDate: myTasksFrom, endDate: myTasksTo };
  };

  // Fetches /operation-stage-histories/assigned-employees for the selected range.
  const fetchMyTasks = async () => {
    const { startDate, endDate } = getMyTasksDateRange();

    if (!startDate || !endDate) {
      setMyTasks([]);
      setMyTasksError("Please select both From and To dates");
      return;
    }
    if (startDate > endDate) {
      setMyTasks([]);
      setMyTasksError("From date cannot be after To date");
      return;
    }

    setMyTasksLoading(true);
    setMyTasksError(null);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_OPERATIONS}/operation-stage-histories/assigned-employees`,
        { params: { startDate, endDate } },
      );
      if (response.data?.status === 200) {
        setMyTasks(response.data.data || []);
      } else {
        setMyTasks([]);
        setMyTasksError(response.data?.message || "Failed to load tasks");
      }
    } catch (err) {
      setMyTasks([]);
      setMyTasksError(err.response?.data?.message || "Failed to load tasks");
      console.error("Error fetching my tasks:", err);
    } finally {
      setMyTasksLoading(false);
    }
  };

  // Opening the modal or changing the range / custom dates triggers the fetch.
  useEffect(() => {
    if (showMyTasks) fetchMyTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMyTasks, myTasksRange, myTasksFrom, myTasksTo]);

  const handleOpenMyTasks = () => {
    setShowMyTasks(true);
  };

  const formatDateTime = (value) =>
    value
      ? new Date(value).toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : "—";

  const handleSelectCategory = (categoryId) => {
    setSelectedCategoryId((prev) => (prev === categoryId ? null : categoryId));
    setSelectedStageId(null);
  };

  const handleSelectStage = (stageId) => {
    setSelectedStageId((prev) => (prev === stageId ? null : stageId));
  };

  const clearFilters = () => {
    setSelectedCategoryId(null);
    setSelectedStageId(null);
  };

  const handleOpenBOMModal = (operation) => {
    const transformedData = {
      enquiryId: operation.enquiryID,
      productOrderHistoryId: operation.productOrderHistoryId,
      productHistories: [
        {
          productId: operation.productId,
          product: {
            name: operation.productName || "Unknown Product",
          },
        },
      ],
      leadName: null,
      clientName: operation.clientName,
      stageName: operation.operationStageName || "Operation Stage",
    };

    setBomOperationData(transformedData);
    setShowBOMModal(true);
  };

  const handleCloseBOMModal = () => {
    setShowBOMModal(false);
    setBomOperationData(null);
  };

  const handleBOMSuccess = () => {
    toast.success("Bill of Materials updated successfully!");
    refreshAll();
  };

  const handleOpenMOM = (operation) => {
    const enquiryId = operation.enquiryID || operation.id;

    setMomModal({
      isOpen: true,
      operationStageHistoryId: operation.id,
      enquiryId: enquiryId,
    });
  };

  const handleCloseMOM = () => {
    setMomModal({
      isOpen: false,
      operationStageHistoryId: null,
      enquiryId: null,
    });

    refreshAll();
    fetchEmployeeCategories();
  };

  const toggleCardExpansion = (operation) => {
    const operationId = operation.id;
    const willExpand = !expandedCards[operationId];

    setExpandedCards((prev) => ({
      ...prev,
      [operationId]: willExpand,
    }));

    if (willExpand && !operation.detailsFetched) {
      fetchOperationFullDetails(operationId);
    }
  };

  // ADD: The Complete button must only be enabled once we actually know the
  // checklist/custom-spec completion state — the lightweight list endpoint
  // doesn't reliably carry it, only /employee/{id} does. Previously that
  // fetch only ran when the user manually expanded a card, so an assigned
  // card with real, unfinished checklist items could show an *enabled*
  // Complete button before it had ever been expanded (empty array read as
  // "nothing to complete"). Auto-fetch full details for every assigned card
  // as soon as it appears, so completion state is known up front.
  useEffect(() => {
    columnItems.assigned.forEach((op) => {
      if (op && op.id != null && !op.detailsFetched) {
        fetchOperationFullDetails(op.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnItems.assigned]);

  // ADD: Toggle a single MOM decision's expanded/collapsed state
  const toggleDecisionExpansion = (decisionKey) => {
    setExpandedDecisions((prev) => ({
      ...prev,
      [decisionKey]: !prev[decisionKey],
    }));
  };

  const handleAssignToMe = async (operationId) => {
    try {
      setProcessingAction(true);
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_OPERATIONS}/operation-stage/${operationId}/accept`,
      );
      if (response.data?.status === 200) {
        toast.success("Job assigned to you successfully!");
        await refreshAll();
      }
    } catch (error) {
      console.error("Error assigning job:", error);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleCompleteJob = async (operationId) => {
    try {
      setProcessingAction(true);
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_OPERATIONS}/operation-stage/${operationId}/complete`,
      );
      if (response.data?.status === 200) {
        toast.success("Job completed successfully!");
        await refreshAll();
      }
    } catch (error) {
      console.error("Error completing job:", error);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReturnJob = async () => {
    if (!selectedOperation || !returnRemarks.trim()) {
      toast.error("Please provide remarks for returning the job");
      return;
    }

    try {
      setProcessingAction(true);
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_OPERATIONS}/operation-stage/${selectedOperation.id}/return`,
        null,
        {
          params: { remarks: returnRemarks },
        },
      );
      if (response.data?.status === 200) {
        toast.success("Job returned successfully!");
        setShowReturnModal(false);
        setReturnRemarks("");
        setSelectedOperation(null);
        await refreshAll();
      }
    } catch (error) {
      console.error("Error returning job:", error);
    } finally {
      setProcessingAction(false);
    }
  };

  // ADD: Checklist/custom-spec completion updates are applied locally via
  // updateOperationInColumns below — no refetch needed, since we already
  // know exactly what changed. But completing one still fires a backend
  // event, which echoes back over the websocket a moment later and would
  // trigger handleWebSocketUpdate's full quiet refetch of all three
  // columns anyway, undoing the point of the local update (new array
  // references, possible reordering, scroll jump). suppressWsRefresh opens
  // a short window right after a local checklist/custom-spec update during
  // which the websocket's onUpdate is a no-op, since we already know it's
  // just our own echo. Genuine remote events (assign/complete/return by
  // someone else, or a checklist change from someone else's session)
  // outside that window still refresh normally.
  const suppressWsRefreshRef = useRef(false);
  const suppressWsRefreshTimeoutRef = useRef(null);
  const suppressWsRefresh = useCallback((durationMs = 3000) => {
    suppressWsRefreshRef.current = true;
    if (suppressWsRefreshTimeoutRef.current) {
      clearTimeout(suppressWsRefreshTimeoutRef.current);
    }
    suppressWsRefreshTimeoutRef.current = setTimeout(() => {
      suppressWsRefreshRef.current = false;
    }, durationMs);
  }, []);
  useEffect(() => {
    return () => {
      if (suppressWsRefreshTimeoutRef.current) {
        clearTimeout(suppressWsRefreshTimeoutRef.current);
      }
    };
  }, []);

  // ADD: Apply a local update to a single operation wherever it currently
  // lives (due/assigned/completed), without touching the other items or
  // re-fetching anything. Used for small in-place mutations (checklist /
  // custom-spec completion) where a full refreshAll() would be overkill —
  // refetching every column from page 0 also truncates any extra pages the
  // user had loaded via "Show more", which both drops the card's already-
  // fetched detail and shrinks the list out from under the scroll position.
  const updateOperationInColumns = (operationId, updater) => {
    const applyUpdate = (op) =>
      op && op.id === operationId ? updater(op) : op;
    setColumnItems((prev) => ({
      due: prev.due.map(applyUpdate),
      assigned: prev.assigned.map(applyUpdate),
      completed: prev.completed.map(applyUpdate),
    }));
  };

  const handleCompleteChecklistItem = async (
    operationId,
    checklistItemId,
    remarks = "",
  ) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_OPERATIONS}/operation-stage/checklist-history/complete`,
        {
          id: checklistItemId,
          remarks: remarks,
          completedByEmployeeId: user?.id,
        },
      );
      if (response.data?.status === 200) {
        toast.success("Checklist item completed!");
        suppressWsRefresh();
        updateOperationInColumns(operationId, (op) => ({
          ...op,
          operationStageCheckListHistories: (
            op.operationStageCheckListHistories || []
          ).map((item) =>
            item.id === checklistItemId
              ? {
                  ...item,
                  completed: true,
                  completedAt: new Date().toISOString(),
                  remarks: remarks || item.remarks,
                }
              : item,
          ),
        }));
        fetchStatusCounts();
      }
    } catch (error) {
      console.error("Error completing checklist item:", error);
      toast.error("Failed to complete checklist item");
    }
  };

  const handleCompleteCustomSpec = async (
    operationId,
    customSpecId,
    remarks,
  ) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_OPERATIONS}/operation-stage/custom-spec-history/complete`,
        {
          id: customSpecId,
          remarks: remarks || "",
        },
      );
      if (response.data?.status === 200) {
        toast.success("Custom specification completed!");
        suppressWsRefresh();
        updateOperationInColumns(operationId, (op) => ({
          ...op,
          customSpecificationHistories: (
            op.customSpecificationHistories || []
          ).map((spec) =>
            spec.id === customSpecId
              ? {
                  ...spec,
                  completed: true,
                  remarks: remarks || spec.remarks,
                }
              : spec,
          ),
        }));
        fetchStatusCounts();
      }
    } catch (error) {
      console.error("Error completing custom specification:", error);
      toast.error("Failed to complete custom specification");
    }
  };

  const isAllTasksCompleted = (operation) => {
    if (!operation) return false;

    const checklistItems = operation.operationStageCheckListHistories || [];
    const customSpecs = operation.customSpecificationHistories || [];

    const allChecklistCompleted =
      checklistItems.length === 0 ||
      checklistItems.every((item) => item?.completed);

    const allCustomSpecsCompleted =
      customSpecs.length === 0 || customSpecs.every((spec) => spec?.completed);

    return allChecklistCompleted && allCustomSpecsCompleted;
  };

  // ADD: Apply the pill filters (category / stage) within a column's
  // already status-filtered (server-side) list.
  const applyPillFilters = (list) =>
    list.filter((op) => {
      if (!op) return false;
      if (selectedCategoryId && op.operationCategoryId !== selectedCategoryId)
        return false;
      if (selectedStageId && op.operationStageId !== selectedStageId)
        return false;
      return true;
    });

  const dueOperations = applyPillFilters(columnItems.due);
  const assignedOperations = applyPillFilters(columnItems.assigned);
  const completedOperations = applyPillFilters(columnItems.completed);

  function capitalizeFirstLetter(str) {
    const lower = str.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  // ADD: Whether an operation is assigned to the logged-in employee
  const isMyOperation = (op) => {
    if (!op || !user) return false;
    const myId = user.empId ?? user.id;
    if (myId == null || op.assignedEmployeeId == null) return false;
    return String(op.assignedEmployeeId) === String(myId);
  };

  // ADD: Split a column's list into "mine" (shown first) and "others"
  const groupMineFirst = (list) => {
    const mine = list.filter(isMyOperation);
    const others = list.filter((op) => !isMyOperation(op));
    return { mine, others, all: [...mine, ...others] };
  };

  const dueGroup = groupMineFirst(dueOperations);
  const assignedGroup = groupMineFirst(assignedOperations);
  const completedGroup = groupMineFirst(completedOperations);

  // ADD: Renders a column's cards with a "My Jobs" section pinned to the top
  const renderColumnCards = (group, emptyIcon, emptyText) => {
    if (group.all.length === 0) {
      return (
        <div className="empty-state">
          <Icon icon={emptyIcon} />
          <p>{emptyText}</p>
        </div>
      );
    }
    return (
      <>
        {group.mine.map(renderKanbanCard)}
        {group.others.map(renderKanbanCard)}
      </>
    );
  };

  // ADD: Bottom-of-column footer. Previously this only ever rendered a
  // spinner while columnLoadingMore was true, with nothing to actually
  // *trigger* the next page other than the scroll handler — so if the
  // column-content element wasn't the thing that scrolled (or the user
  // just wanted to click something instead of scrolling), "more" never
  // loaded. Now it also renders an explicit "Show more" button whenever
  // hasMore is true, which calls fetchColumn directly.
  const renderPaginationFooter = (columnItemCount, columnKey) => {
    if (columnItemCount === 0) return null;

    if (columnLoadingMore[columnKey]) {
      return (
        <div className="column-loading-more">
          <Icon icon="mdi:loading" />
          <span>Loading more…</span>
        </div>
      );
    }

    if (columnPagination[columnKey]?.hasMore) {
      return (
        <button
          type="button"
          className="column-show-more"
          onClick={() => fetchColumn(columnKey, false)}
        >
          <span>Show more</span>
          <ChevronDown size={12} />
        </button>
      );
    }

    return null;
  };

  // ADD: My Tasks list after "only mine" + status filters.
  // Pending = anything that is not COMPLETED.
  const visibleMyTasks = myTasks.filter((t) => {
    if (myTasksOnlyMine) {
      const myId = user?.empId ?? user?.id;
      if (myId == null || String(t.assignedEmployeeId) !== String(myId))
        return false;
    }
    const isCompleted = (t.status || "").toUpperCase() === "COMPLETED";
    if (myTasksStatusFilter === "COMPLETED") return isCompleted;
    if (myTasksStatusFilter === "PENDING") return !isCompleted;
    return true;
  });

  // ADD: Which kanban columns are visible for the selected status filter
  const showDueColumn = statusFilter !== "COMPLETED";
  const showAssignedColumn = statusFilter !== "COMPLETED";
  const showCompletedColumn = statusFilter !== "PENDING";

  const selectedCategory = employeeCategories.find(
    (cat) => cat.operationCategoryId === selectedCategoryId,
  );

  const renderKanbanCard = (operation) => {
    if (!operation) return null;

    const status = operation.operationStatus;
    const checklistItems = operation.operationStageCheckListHistories || [];
    const customSpecs = operation.customSpecificationHistories || [];
    const productSpecs = operation.productSpecificationHistories || [];
    const allTasksCompleted = isAllTasksCompleted(operation);
    const totalTasks = checklistItems.length + customSpecs.length;
    const completedTasks =
      checklistItems.filter((item) => item?.completed).length +
      customSpecs.filter((spec) => spec?.completed).length;

    const isExpanded = expandedCards[operation.id] || false;
    const isMine = isMyOperation(operation);
    const isFetchingDetails = detailsLoading[operation.id] || false;
    // Completion state (checklist + custom specs) is only reliable once the
    // full-details fetch has landed — until then, an empty checklist array
    // could just mean "not fetched yet" rather than "nothing to complete".
    const taskStateKnown = status !== "ASSIGNED" || operation.detailsFetched;
    const momTopics = operation.minutesOfMeeting?.topics || [];

    return (
      <div key={operation.id} className={`kanban-card ${isMine ? "mine" : ""}`}>
        {/* ── ROW 1: product name + type badge ── */}
        <div className="card-header">
          <div className="card-title-row">
            <h4>{operation.productName || "Unknown Product"}</h4>
            {operation.productType && (
              <span
                className={`badge badge-${operation.productType.toLowerCase()}`}
              >
                {capitalizeFirstLetter(operation.productType)}
              </span>
            )}
            {status === "RETURNED" && (
              <span className="badge badge-returned">
                <Icon icon="mdi:undo-variant" />
                Returned
              </span>
            )}
          </div>

          {/* ── ROW 2: codes + qty + MOM/BOM buttons ── */}
          <div className="card-meta-row">
            <div className="card-codes">
              {operation.productCode && <span>#{operation.productCode}</span>}
              {operation.purchaseOrderCode && (
                <span>PO·{operation.purchaseOrderCode}</span>
              )}
              {operation.clientName && <span>{operation.clientName}</span>}
              <span className="card-quantity">
                Qty {operation.quantity || 0}
              </span>
            </div>
            <div className="card-actions-top">
              {(status === "PROCESSING" || status === "RETURNED") && (
                <button
                  className="assign-btn"
                  onClick={() => handleAssignToMe(operation.id)}
                >
                  <Icon icon="mdi:account-plus" />
                  <span>Assign to self</span>
                </button>
              )}

              <button
                className="mom-btn"
                onClick={() => handleOpenMOM(operation)}
              >
                <Speech size={10} />
                <span>MOM</span>
              </button>

              <button
                className="bom-btn"
                onClick={() => handleOpenBOMModal(operation)}
              >
                <FileText size={10} />
                <span>BOM</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── BODY: stage row + info chips ── */}
        <div className="card-body">
          {/* Stage name + step number */}
          {(operation.operationStageName || operation.stageName) && (
            <div className="card-stage-row">
              <Icon icon="mdi:stairs" />
              <span className="stage-name">
                {operation.operationStageName || operation.stageName}
              </span>
              {operation.sequenceNumber && (
                <span className="stage-seq">
                  Step {operation.sequenceNumber}
                </span>
              )}
            </div>
          )}

          {/* ── Return reason — always visible for returned jobs ── */}
          {operation.remarks && (
            <div className="card-remarks">
              <Icon icon="mdi:message-alert-outline" />
              <div>
                <span className="return-remarks-label">Remark</span>
                <p>{operation.remarks}</p>
              </div>
            </div>
          )}

          {/* ── Returned message — from the return action itself ── */}
          {status === "RETURNED" && operation.returnedMessage && (
            <div className="card-return">
              <Icon icon="mdi:undo-variant" />
              <div>
                <span className="return-remarks-label">Returned message</span>
                <p>{operation.returnedMessage}</p>
              </div>
            </div>
          )}

          {/* Inline chips: employee, dates */}
          {(operation.assignedEmployeeName ||
            operation.dueAt ||
            operation.startedAt ||
            operation.endedAt) && (
            <div className="card-info-chips">
              {operation.assignedEmployeeName && (
                <span className="chip chip-employee">
                  <Icon icon="mdi:account" />
                  {operation.assignedEmployeeName}
                </span>
              )}
              {operation.dueAt && (
                <span className="chip chip-due">
                  <Icon icon="mdi:calendar-clock" />
                  {new Date(operation.dueAt).toLocaleDateString()}
                </span>
              )}
              {operation.startedAt && (
                <span className="chip chip-started">
                  <Icon icon="mdi:play-circle-outline" />
                  {new Date(operation.startedAt).toLocaleDateString()}
                </span>
              )}
              {operation.endedAt && (
                <span className="chip chip-started">
                  <Icon icon="mdi:check-circle-outline" />
                  {new Date(operation.endedAt).toLocaleDateString()}
                </span>
              )}
            </div>
          )}

          {/* ── MOM Decisions — highlighted, each individually expandable ── */}
          {Array.isArray(operation.minutesOfMeeting?.decisions) &&
            operation.minutesOfMeeting.decisions.length > 0 && (
              <div className="card-mom-decisions">
                <div className="mom-decisions-header">
                  <Icon icon="mdi:lightbulb-on" />
                  <span>
                    Meeting Decision
                    {operation.minutesOfMeeting.decisions.length > 1 ? "s" : ""}
                  </span>
                  {operation.minutesOfMeeting.status && (
                    <span
                      className={`mom-status-badge ${operation.minutesOfMeeting.status.toLowerCase()}`}
                    >
                      {capitalizeFirstLetter(operation.minutesOfMeeting.status)}
                    </span>
                  )}
                </div>

                {operation.minutesOfMeeting.decisions.map(
                  (decision, decisionIndex) => {
                    // NOTE: API returns the text under `decisions` (singular
                    // decision object, plural-named field). Other keys kept
                    // as fallbacks in case the shape varies elsewhere.
                    const decisionText =
                      typeof decision === "string"
                        ? decision
                        : decision?.decisions ||
                          decision?.decision ||
                          decision?.text ||
                          decision?.title ||
                          decision?.description ||
                          "";

                    if (!decisionText) return null;

                    const decisionKey = `${operation.id}-decision-${
                      decision?.id ?? decisionIndex
                    }`;
                    const isLongDecision =
                      decisionText.length > DECISION_TRUNCATE_LENGTH;
                    const isDecisionExpanded =
                      expandedDecisions[decisionKey] || false;

                    return (
                      <div
                        key={decisionKey}
                        className={`mom-decision-item ${
                          isLongDecision ? "clickable" : ""
                        }`}
                        onClick={
                          isLongDecision
                            ? () => toggleDecisionExpansion(decisionKey)
                            : undefined
                        }
                        title={
                          isLongDecision && !isDecisionExpanded
                            ? decisionText
                            : undefined
                        }
                      >
                        <p
                          className={
                            isLongDecision && !isDecisionExpanded
                              ? "clamped"
                              : ""
                          }
                        >
                          {decisionText}
                        </p>
                        {isLongDecision && (
                          <span className="mom-decision-toggle">
                            {isDecisionExpanded
                              ? "Show less"
                              : "Click to expand"}
                            <ChevronDown
                              size={10}
                              className={isDecisionExpanded ? "rotated" : ""}
                            />
                          </span>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            )}
        </div>

        {/* ── EXPAND TOGGLE — compact bar. Always available: expanding    ── */}
        {/* fetches the full record the first time, so specs/remarks that   */}
        {/* aren't in the lightweight list yet can still be shown.          */}
        <button
          className={`card-expand-toggle ${isExpanded ? "expanded" : ""} ${
            isFetchingDetails ? "loading-details" : ""
          }`}
          onClick={() => toggleCardExpansion(operation)}
          disabled={isFetchingDetails}
        >
          {isFetchingDetails ? (
            <>
              <span>Loading details…</span>
              <Icon icon="mdi:loading" className="detail-spinner" />
            </>
          ) : (
            <>
              <span>{isExpanded ? "Hide details" : "Show details"}</span>
              <ChevronDown size={11} />
            </>
          )}
        </button>

        {/* COLLAPSIBLE */}
        <div className={`card-collapsible ${isExpanded ? "" : "collapsed"}`}>
          {isExpanded && isFetchingDetails && !operation.detailsFetched && (
            <div className="card-details-loading">
              <Icon icon="mdi:loading" />
              <span>Fetching full details…</span>
            </div>
          )}

          {productSpecs.length > 0 && (
            <div className="card-specifications">
              <div className="specs-header">
                <Icon icon="mdi:clipboard-text-outline" />
                <span>Product Specifications</span>
              </div>

              <div className="specs-list">
                {productSpecs.map((spec) => (
                  <div key={spec.id} className="spec-item">
                    <span className="spec-name">{spec.specificationName}:</span>
                    <span className="spec-value">
                      {spec.specificationValue}
                      {spec.unit && ` ${spec.unit}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {operation.remarks && status !== "RETURNED" && (
            <div className="card-remarks">
              <Icon icon="mdi:note-text-outline" />
              <p>{operation.remarks}</p>
            </div>
          )}

          {/* ── Full meeting topics — only available after the detail   ── */}
          {/* fetch triggered by expanding this card has completed.       */}
          {operation.detailsFetched && momTopics.length > 0 && (
            <div className="card-mom-topics">
              <div className="mom-topics-header">
                <Icon icon="mdi:message-text-outline" />
                <span>Meeting Topics ({momTopics.length})</span>
              </div>
              {momTopics.map((topic, topicIndex) => (
                <div
                  key={topic?.topicId ?? topicIndex}
                  className="mom-topic-item"
                >
                  <div className="mom-topic-meta">
                    {topic?.contactPersonName && (
                      <span>With: {topic.contactPersonName}</span>
                    )}
                    {topic?.employeeName && (
                      <span>By: {topic.employeeName}</span>
                    )}
                  </div>
                  {topic?.topicDetails && <p>{topic.topicDetails}</p>}
                  {(() => {
                    // API returns the topic's action list as `actions`
                    // (see the employee/{id} sample response); keep the
                    // ResponseDTOList name as a fallback in case another
                    // endpoint variant uses it.
                    const topicActions =
                      topic?.actions || topic?.actionsResponseDTOList || [];
                    if (
                      !Array.isArray(topicActions) ||
                      topicActions.length === 0
                    )
                      return null;
                    return (
                      <div className="mom-topic-actions">
                        {topicActions.map((action, actionIndex) => (
                          <div
                            key={action?.id ?? actionIndex}
                            className="mom-action-row"
                          >
                            <span className="action-status">
                              {capitalizeFirstLetter(
                                action?.status || "Planned",
                              )}
                            </span>
                            {/* API field is `action` (singular) — `actions`
                                on the action object itself was never real */}
                            <span>{action?.action || action?.actions}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>

        {status === "ASSIGNED" && totalTasks > 0 && (
          <div className="card-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${(completedTasks / totalTasks) * 100}%`,
                }}
              ></div>
            </div>
            <span className="progress-text">
              {completedTasks}/{totalTasks} tasks completed
            </span>
          </div>
        )}

        {status === "ASSIGNED" && (
          <div className="card-tasks">
            {customSpecs.length > 0 && (
              <div className="task-group">
                <div className="task-group-title">
                  <Icon icon="mdi:star" />
                  Custom Specifications (
                  {customSpecs.filter((s) => s.completed).length}/
                  {customSpecs.length})
                </div>
                {customSpecs.map((spec) => {
                  if (!spec) return null;
                  return (
                    <div
                      key={spec.id}
                      className={`task-item ${
                        spec.completed ? "completed" : ""
                      }`}
                    >
                      <div className="task-content">
                        <input
                          type="checkbox"
                          checked={spec.completed || false}
                          disabled
                        />
                        <div className="task-details">
                          <span className="task-name">
                            {spec.specs ||
                              spec.specificationName ||
                              spec.specName ||
                              spec.name ||
                              "No specification"}
                          </span>
                          {spec.remarks && (
                            <span className="task-remarks">
                              Remarks: {spec.remarks}
                            </span>
                          )}
                        </div>
                      </div>
                      {!spec.completed && (
                        <div className="task-action">
                          <input
                            type="text"
                            placeholder="Add remarks"
                            className="task-input"
                            id={`spec-${spec.id}`}
                          />
                          <IconButton
                            icon="mdi:check"
                            onClick={() => {
                              const input = document.getElementById(
                                `spec-${spec.id}`,
                              );
                              handleCompleteCustomSpec(
                                operation.id,
                                spec.id,
                                input?.value || "",
                              );
                            }}
                            variant="success"
                            size="sm"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {checklistItems.length > 0 && (
              <div className="task-group">
                <div className="task-group-title">
                  <Icon icon="mdi:checkbox-marked-outline" />
                  Checklist ({checklistItems.filter((i) => i.completed).length}/
                  {checklistItems.length})
                </div>
                {checklistItems.map((item) => {
                  if (!item) return null;
                  return (
                    <div
                      key={item.id}
                      className={`task-item ${
                        item.completed ? "completed" : ""
                      }`}
                    >
                      <div className="task-content">
                        <input
                          type="checkbox"
                          checked={item.completed || false}
                          onChange={() =>
                            !item.completed &&
                            handleCompleteChecklistItem(operation.id, item.id)
                          }
                          disabled={item.completed}
                        />
                        <div className="task-details">
                          <span className="task-name">
                            {item.taskName ||
                              item.task ||
                              item.name ||
                              item.checklistName ||
                              item.itemName ||
                              item.description ||
                              "No task name"}
                          </span>
                          {item.completedAt && (
                            <span className="task-completed-time">
                              Completed:{" "}
                              {new Date(item.completedAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                      {!item.completed && (
                        <div className="task-action">
                          <input
                            type="text"
                            placeholder="Add remarks"
                            className="task-input"
                            id={`check-${item.id}`}
                          />
                          <IconButton
                            icon="mdi:check"
                            onClick={() => {
                              const input = document.getElementById(
                                `check-${item.id}`,
                              );
                              handleCompleteChecklistItem(
                                operation.id,
                                item.id,
                                input?.value || "",
                              );
                            }}
                            variant="success"
                            size="sm"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="card-actions">
          {status === "ASSIGNED" && (
            <>
              <ButtonWithIcon
                icon="mdi:undo-variant"
                text="Return"
                onClick={() => {
                  setSelectedOperation(operation);
                  setShowReturnModal(true);
                }}
                disabled={processingAction}
                className="warning return-job-btn"
              />

              <ButtonWithIcon
                icon="mdi:check-bold"
                text={
                  !taskStateKnown
                    ? "Checking tasks…"
                    : allTasksCompleted
                      ? "Complete"
                      : `${completedTasks}/${totalTasks}`
                }
                onClick={() => {
                  if (!taskStateKnown || !allTasksCompleted) {
                    toast.warning("Please complete all tasks first!");
                    return;
                  }
                  handleCompleteJob(operation.id);
                }}
                disabled={
                  processingAction || !taskStateKnown || !allTasksCompleted
                }
                className="success complete-job-btn"
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const totalLoadedItems =
    columnItems.due.length +
    columnItems.assigned.length +
    columnItems.completed.length;

  if (loading && totalLoadedItems === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="operation-stages-container">
      <div className="header">
        <div className="header-left">
          <h1>Operation Processing</h1>
          <p className="subtitle">
            Monitor and manage all production operations from start to finish.
          </p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="my-tasks-btn"
            onClick={handleOpenMyTasks}
          >
            <Icon icon="mdi:clipboard-account-outline" />
            <span>My Tasks</span>
          </button>
          {isWebSocketConnected ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 10px",
                borderRadius: 999,
                background: "#dcfce7",
                color: "#15803d",
                border: "1px solid #86efac",
                fontSize: "0.85rem",
                fontWeight: 600,
                width: "fit-content",
              }}
            >
              <Icon icon="mdi:access-point" />
              <span>Live Updates</span>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 10px",
                borderRadius: 999,
                background: "#f1f5f9",
                color: "#64748b",
                border: "1px solid #cbd5e1",
                fontSize: "0.85rem",
                fontWeight: 600,
                width: "fit-content",
              }}
            >
              <Icon icon="mdi:access-point-off" />
              <span>Connecting…</span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">
            ×
          </button>
        </div>
      )}

      {/* ── Pill filters (operation categories mapped to the logged-in employee) ── */}
      <div className="filters-bar">
        <div className="pill-row status-row">
          <Icon icon="mdi:list-status" className="filter-icon" />
          {[
            { key: "ALL", label: "All" },
            { key: "PENDING", label: "Pending" },
            { key: "COMPLETED", label: "Completed" },
          ].map((opt) => (
            <button
              key={opt.key}
              className={`pill ${statusFilter === opt.key ? "active" : ""}`}
              onClick={() => setStatusFilter(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {(employeeCategories.length > 0 || loadingCategories) && (
          <>
            <div className="pill-row sub-row">
              <Icon icon="mdi:filter-variant" className="filter-icon" />
              <button
                className={`pill ${!selectedCategoryId ? "active" : ""}`}
                onClick={clearFilters}
              >
                All Assigned Categories
              </button>
              {employeeCategories.map((cat) => (
                <button
                  key={cat.operationCategoryId}
                  className={`pill ${
                    selectedCategoryId === cat.operationCategoryId
                      ? "active"
                      : ""
                  }`}
                  onClick={() => handleSelectCategory(cat.operationCategoryId)}
                >
                  {cat.operationCategoryName ||
                    cat.operationCatogeryName ||
                    `Category ${cat.operationCategoryId}`}
                </button>
              ))}
              {loadingCategories && (
                <Icon icon="mdi:loading" className="filter-loading spin" />
              )}
            </div>

            {selectedCategory &&
              selectedCategory.operationStages?.length > 0 && (
                <div className="pill-row sub-row">
                  <button
                    className={`pill pill-sm ${!selectedStageId ? "active" : ""}`}
                    onClick={() => setSelectedStageId(null)}
                  >
                    All Stages
                  </button>
                  {selectedCategory.operationStages.map((stage) => (
                    <button
                      key={stage.operationStageId}
                      className={`pill pill-sm ${
                        selectedStageId === stage.operationStageId
                          ? "active"
                          : ""
                      }`}
                      onClick={() => handleSelectStage(stage.operationStageId)}
                    >
                      {stage.operationStageName}
                    </button>
                  ))}
                </div>
              )}

            {(selectedCategoryId || selectedStageId) && (
              <button className="clear-filters-btn" onClick={clearFilters}>
                <Icon icon="mdi:close-circle-outline" />
                Clear filters
              </button>
            )}
          </>
        )}
      </div>

      <div className="kanban-board">
        {showDueColumn && (
          <div className="kanban-column">
            <div className="column-header due">
              <Icon icon="mdi:clock-outline" />
              <span>Due</span>
              <span className="column-count">
                {(statusCounts.PROCESSING || 0) + (statusCounts.RETURNED || 0)}
              </span>
            </div>
            <div
              className="column-content"
              onScroll={handleColumnScroll("due")}
            >
              {renderColumnCards(dueGroup, "mdi:check-all", "No pending jobs")}
              {renderPaginationFooter(dueOperations.length, "due")}
            </div>
          </div>
        )}

        {showAssignedColumn && (
          <div className="kanban-column">
            <div className="column-header assigned">
              <Icon icon="mdi:account-check" />
              <span>Assigned</span>
              <span className="column-count">{statusCounts.ASSIGNED || 0}</span>
            </div>
            <div
              className="column-content"
              onScroll={handleColumnScroll("assigned")}
            >
              {renderColumnCards(
                assignedGroup,
                "mdi:briefcase-outline",
                "No assigned jobs",
              )}
              {renderPaginationFooter(assignedOperations.length, "assigned")}
            </div>
          </div>
        )}

        {showCompletedColumn && (
          <div className="kanban-column">
            <div className="column-header completed">
              <Icon icon="mdi:check-circle" />
              <span>Completed</span>
              <span className="column-count">
                {statusCounts.COMPLETED || 0}
              </span>
            </div>
            <div
              className="column-content"
              onScroll={handleColumnScroll("completed")}
            >
              {renderColumnCards(
                completedGroup,
                "mdi:clipboard-check-outline",
                "No completed jobs",
              )}
              {renderPaginationFooter(completedOperations.length, "completed")}
            </div>
          </div>
        )}
      </div>

      {/* My Tasks Modal */}
      {showMyTasks && (
        <div className="modal-overlay" onClick={() => setShowMyTasks(false)}>
          <div
            className="modal-content my-tasks-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>My Tasks</h2>
              <button
                className="submit-button"
                onClick={() => setShowMyTasks(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="my-tasks-toolbar my-tasks-range">
                <div className="my-tasks-pills">
                  {[
                    { key: "WTD", label: "WTD" },
                    { key: "MTD", label: "MTD" },
                    { key: "YTD", label: "YTD" },
                    { key: "CUSTOM", label: "Custom" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      className={`pill ${
                        myTasksRange === opt.key ? "active" : ""
                      }`}
                      onClick={() => setMyTasksRange(opt.key)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {myTasksRange === "CUSTOM" && (
                  <div className="my-tasks-custom-dates">
                    <label>
                      From
                      <input
                        type="date"
                        value={myTasksFrom}
                        max={myTasksTo || undefined}
                        onChange={(e) => setMyTasksFrom(e.target.value)}
                      />
                    </label>
                    <label>
                      To
                      <input
                        type="date"
                        value={myTasksTo}
                        min={myTasksFrom || undefined}
                        onChange={(e) => setMyTasksTo(e.target.value)}
                      />
                    </label>
                  </div>
                )}
              </div>

              <div className="my-tasks-toolbar">
                <div className="my-tasks-pills">
                  {[
                    { key: "ALL", label: "All" },
                    { key: "PENDING", label: "Pending" },
                    { key: "COMPLETED", label: "Completed" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      className={`pill ${
                        myTasksStatusFilter === opt.key ? "active" : ""
                      }`}
                      onClick={() => setMyTasksStatusFilter(opt.key)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <label className="my-tasks-only-mine">
                  <input
                    type="checkbox"
                    checked={myTasksOnlyMine}
                    onChange={(e) => setMyTasksOnlyMine(e.target.checked)}
                  />
                  Only my tasks
                </label>
              </div>

              {myTasksLoading ? (
                <div className="my-tasks-state">
                  <Icon icon="mdi:loading" className="spin" />
                  <span>Loading tasks…</span>
                </div>
              ) : myTasksError ? (
                <div className="my-tasks-state error">{myTasksError}</div>
              ) : visibleMyTasks.length === 0 ? (
                <div className="my-tasks-state">No tasks found</div>
              ) : (
                <div className="my-tasks-table-wrap">
                  <table className="my-tasks-table">
                    <thead>
                      <tr>
                        <th>Stage</th>
                        <th>Assigned To</th>
                        <th>Status</th>
                        <th>Assigned At</th>
                        <th>Started At</th>
                        <th>Ended At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleMyTasks.map((t) => (
                        <tr key={t.operationStageHistoryId}>
                          <td>{t.operationStageName || "—"}</td>
                          <td>{t.assignedEmployeeName || "—"}</td>
                          <td>
                            <span
                              className={`task-status ${(
                                t.status || ""
                              ).toLowerCase()}`}
                            >
                              {t.status ? capitalizeFirstLetter(t.status) : "—"}
                            </span>
                          </td>
                          <td>{formatDateTime(t.operationsAssignedAt)}</td>
                          <td>{formatDateTime(t.operationsStartedAt)}</td>
                          <td>{formatDateTime(t.operationsEndedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Return Job Modal */}
      {showReturnModal && selectedOperation && (
        <div
          className="modal-overlay"
          onClick={() => setShowReturnModal(false)}
        >
          <div
            className="modal-content return-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Return Job</h2>
              <button
                className="submit-button"
                onClick={() => setShowReturnModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p className="return-warning">
                You are about to return the job for{" "}
                <strong>
                  {selectedOperation.productName || "Unknown Product"}
                </strong>
                . Please provide remarks explaining why this job is being
                returned.
              </p>
              <div className="form-group">
                <label>Remarks *</label>
                <textarea
                  value={returnRemarks}
                  onChange={(e) => setReturnRemarks(e.target.value)}
                  placeholder="Enter your remarks here..."
                  rows="4"
                  required
                />
              </div>
            </div>

            <div className="modal-footer">
              <ButtonWithIcon
                icon="mdi:cancel"
                text="Cancel"
                onClick={() => {
                  setShowReturnModal(false);
                  setReturnRemarks("");
                }}
                className="cancel-button"
                variant="secondary"
              />
              <ButtonWithIcon
                icon="mdi:undo-variant"
                text="Return Job"
                onClick={handleReturnJob}
                variant="warning"
                disabled={!returnRemarks.trim() || processingAction}
                className="submit-button"
              />
            </div>
          </div>
        </div>
      )}

      {/* BOM Modal */}
      <EnquiryBOMModal
        isOpen={showBOMModal}
        onClose={handleCloseBOMModal}
        enquiryData={bomOperationData}
        axiosInstance={axiosInstance}
        onSuccess={handleBOMSuccess}
      />

      {/* MOM Modal */}
      {momModal.isOpen && (
        <div className="modal-overlay">
          <MinutesOfMeetingManager
            enquiryId={momModal.enquiryId}
            enquiryStageHistoryId={momModal.operationStageHistoryId}
            axiosInstance={axiosInstance}
            getApiEndpoint={getApiEndpoint}
            onClose={handleCloseMOM}
            isOperationMode={true}
            showDiscussionTopics={false}
          />
        </div>
      )}
    </div>
  );
};

export default OperationStages;
